import React from 'react';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { useGlobalSettings } from '@/hooks';

interface GoogleAuthProviderProps {
  children: React.ReactNode;
}

export const GoogleAuthProvider: React.FC<GoogleAuthProviderProps> = ({ children }) => {
  const { data: settings, isLoading, error } = useGlobalSettings();

  // Show loading state while fetching settings
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-gray-300 border-t-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg font-medium">Loading authentication...</p>
          <p className="text-gray-400 text-sm mt-1">Setting up Google OAuth</p>
        </div>
      </div>
    );
  }

  // Show error state only if settings fetch failed entirely
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md mx-auto p-6 animate-fade-in">
          <div className="bg-red-50 border border-red-200 rounded-md p-6 shadow-lg transform transition-all duration-300 hover:scale-105">
            <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-red-800 mb-2">Authentication Error</h3>
            <p className="text-red-600 mb-2">Failed to load authentication settings.</p>
            <p className="text-sm text-red-500">
              Please contact your administrator.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // If Google auth is configured, wrap with GoogleOAuthProvider
  if (settings?.google_auth_key) {
    return (
      <GoogleOAuthProvider clientId={settings.google_auth_key}>
        {children}
      </GoogleOAuthProvider>
    );
  }

  // No Google auth — render children without provider (email auth only)
  return <>{children}</>;
};
